// Property → Visits (/app/property?sub=visits; /app/inspections redirects here): every inspection in the user's scope —
// periodic visits, move-in / move-out, fire doors, snagging, handovers — with where, when and who. A row opens the
// development's Property management → Inspections, where a visit is booked and completed. ?status= ?overdue= ?propertyId=
// (old links) start the filters there.
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useList } from '../../hooks/useList';
import { useCount } from '../../hooks/useCount';
import { useStatFilter } from '../../hooks/useStatFilter';
import { StatTile, FilterNote } from '../charts/StatTile';
import { DataTable, type Column } from '../ui/DataTable';
import { PageHeader, Badge, Select, DateText } from '../ui';
import { humanise } from '../../lib/propertyManagement';

type I = Record<string, any>;
const TILES = {
  scheduled: { label: 'Booked', params: { status: 'SCHEDULED' } },
  overdue: { label: 'Past their date', params: { overdue: true } },
  done: { label: 'Completed', params: { status: 'COMPLETED' } },
} as const;
type TileKey = keyof typeof TILES;
const TYPES = ['PERIODIC', 'MOVE_IN', 'MOVE_OUT', 'FIRE_DOOR', 'SNAGGING', 'HANDOVER'];
const STATUSES = ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];
const visitsOf = (i: I) => `/app/properties/${i.propertyId}?tab=management&sub=inspections${i.unitId ? `&unit=${i.unitId}` : ''}`;

export function VisitsTab() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const tf = useStatFilter<TileKey>();
  const tile = tf.active && TILES[tf.active] ? TILES[tf.active] : null;
  const list = useList<I>('/inspections', { status: sp.get('status') ?? undefined, overdue: sp.get('overdue') === 'true' || undefined, propertyId: sp.get('propertyId') ?? undefined }, { extra: tile?.params });
  const counts = { scheduled: useCount('/inspections', TILES.scheduled.params), overdue: useCount('/inspections', TILES.overdue.params), done: useCount('/inspections', TILES.done.params) };

  const columns: Column<I>[] = [
    { key: 'propertyName', label: 'Development', render: (i) => <Link to={visitsOf(i)} onClick={(e) => e.stopPropagation()} className="font-medium text-ink-800 hover:text-bronze-700 hover:underline">{i.propertyName}</Link> },
    { key: 'unitReference', label: 'Unit', render: (i) => i.unitReference ?? <span className="text-ink-400">Building & communal</span> },
    { key: 'inspectionType', label: 'Type', render: (i) => humanise(i.inspectionType) },
    { key: 'scheduledFor', label: 'Due', sortValue: (i) => i.scheduledFor, render: (i) => <span className={i.status === 'SCHEDULED' && i.scheduledFor && i.scheduledFor < new Date().toISOString().slice(0, 10) ? 'font-medium text-red-600' : ''}><DateText value={i.scheduledFor} /></span> },
    { key: 'status', label: 'Status', render: (i) => <Badge value={i.status} /> },
    { key: 'inspectorName', label: 'Assignee', hideOnMobile: true, render: (i) => i.inspectorName?.trim() || <span className="text-ink-400">Unassigned</span> },
  ];

  return (
    <div>
      <PageHeader title="Visits & inspections" />
      <div className="mb-2.5 tile-row">
        {(Object.keys(TILES) as TileKey[]).map((k) => (
          <StatTile key={k} compact label={TILES[k].label} value={counts[k] ?? '—'} status={k === 'overdue' && counts[k] ? 'warning' : undefined} {...tf.tile(k)} />
        ))}
      </div>
      {tile && <div className="mb-3"><FilterNote label={tile.label} count={list.meta?.total} onClear={tf.clear} /></div>}
      <DataTable exportCsv={{ path: '/inspections', page: 'inspections', query: list.query }} columns={columns} rows={list.rows} meta={list.meta} page={list.page} onPage={list.setPage}
        isLoading={list.isLoading} isFetching={list.isFetching} rowKey={(i) => i.id} onRowClick={(i) => navigate(visitsOf(i))}
        emptyTitle="No visits match" emptyBody="Book a periodic visit from a development's Property management → Visits."
        filters={<>
          <Select value={String(list.filters.inspectionType ?? '')} onChange={(e) => list.setFilter('inspectionType', e.target.value)}><option value="">Any type</option>{TYPES.map((t) => <option key={t} value={t}>{humanise(t)}</option>)}</Select>
          <Select value={String(list.filters.status ?? '')} onChange={(e) => list.setFilter('status', e.target.value)}><option value="">Any status</option>{STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}</Select>
        </>} />
    </div>
  );
}
